/** 页签主题对应权限树里的页面 name（路由 name） */
const THEME_PAGE = {
  organization: 'organization',
  mixingProduction: 'mixingProduction',
  officialCar: 'officialCar',
  contractor: 'contractor',
  builder: 'builder',
  contract: 'contract',
  attendance: 'attendanceStatistics',
  salaryPay: 'salaryPayment',
  progress: 'progress',
  safetyInput: 'safetyInput',
  onlineExam: 'onlineExam',
  trainingMaterials: 'trainingMaterials',
  role: 'rights',
  user: 'user',
  pages: 'systemPage',
  attendanceSet: 'attendanceSetting',
  task: 'todoTakas',
  report: 'dataReport',
  proRealnameReport: 'proRealnameReport',
  preClassEduReport: 'preClassEduReport',
  fields: 'systemFields',
  systemFields: 'systemFields'
}

export function isTabPage(item) {
  if (!item || !item.meta) return false
  return item.meta.tabPage === true || item.meta.tabPage === 'true'
}

/** 把页签子页面从菜单里拿掉；只授权一个时，外层名称和说明改成该页签 */
export function applyTabPermissions(pages) {
  const visit = (list) => {
    (list || []).forEach((node) => {
      const children = node.children || []
      const tabs = children.filter(isTabPage)
      const rest = children.filter((item) => !isTabPage(item))
      const split = node.meta && (node.meta.tabSplit === true || node.meta.tabSplit === 'true')
      if (tabs.length || split) {
        node.tabPages = tabs
        if (tabs.length === 1) {
          const only = tabs[0]
          const meta = only.meta || {}
          node.meta = Object.assign({}, node.meta || {})
          node.meta.title = meta.title || only.pageName || node.meta.title
          node.meta.explain = meta.explain || ''
          node.meta.singleTab = true
        }
        node.children = rest.length ? rest : null
      }
      visit(rest)
    })
  }
  visit(pages)
  return pages
}

export function findPage(pages, name) {
  let found = null
  const visit = (list) => {
    (list || []).forEach((node) => {
      if (found) return
      if (node.name === name) {
        found = node
        return
      }
      visit(node.children)
    })
  }
  visit(pages)
  return found
}

function childTabs(node) {
  if (!node) return []
  if (Array.isArray(node.tabPages)) return node.tabPages
  return (node.children || []).filter(isTabPage)
}

function tabMatches(item, tabLabel, aliases) {
  if (!item || !tabLabel) return false
  const meta = item.meta || {}
  const values = [
    meta.tabKey,
    meta.title,
    item.pageName
  ].concat(aliases || [])
  return values.some((value) => value && String(value) === String(tabLabel))
}

/**
 * Resolve the leaf permission page for the currently displayed business tab.
 * Parent nodes are containers; their buttons belong to the selected leaf page.
 */
export function resolveRuntimePermissionPage(pages, theme, tabLabel) {
  const parentCode = THEME_PAGE[theme] || theme || ''
  if (!parentCode) return ''
  const parent = findPage(pages, parentCode)
  if (!parent) return parentCode
  const tabs = childTabs(parent)
  if (tabLabel) {
    const aliases = TAB_LABEL_ALIASES[tabLabel] || []
    const selected = tabs.find((item) => tabMatches(item, tabLabel, aliases))
    if (selected) return selected.name || selected.pageCode || parentCode
  }
  const split =
    parent.meta &&
    (parent.meta.tabSplit === true || parent.meta.tabSplit === 'true')
  if (split && tabs.length === 1) {
    return tabs[0].name || tabs[0].pageCode || parentCode
  }
  return parent.name || parent.pageCode || parentCode
}

const ROUTE_PAGE = {
  todoTask: 'todoTakas'
}

/**
 * Tab 界面文案与权限树旧标签兼容。
 * 界面可改名（如「固定设备」→「固定机械」），权限/库内标识过渡期并存。
 */
const TAB_LABEL_ALIASES = {
  固定机械: ['固定设备'],
  固定设备: ['固定机械']
}

function expandTabKeys(key, into) {
  if (!key) return
  into.add(key)
  const aliases = TAB_LABEL_ALIASES[key]
  if (!aliases) return
  aliases.forEach((alias) => into.add(alias))
}

export function singleTabTitle(pages, routeName) {
  const node = findPage(pages, ROUTE_PAGE[routeName] || routeName)
  if (!node || !node.meta || !node.meta.singleTab) return ''
  return node.meta.title || ''
}

export function filterPageTabs(tabs, pages, theme) {
  if (!Array.isArray(tabs) || !tabs.length) return tabs
  const node = findPage(pages, THEME_PAGE[theme] || theme)
  if (!node || !node.meta) return tabs
  const split = node.meta.tabSplit === true || node.meta.tabSplit === 'true'
  if (!split) return tabs
  const allowed = new Set()
  ;(node.tabPages || []).forEach((item) => {
    const meta = item.meta || {}
    expandTabKeys(meta.tabKey, allowed)
    expandTabKeys(meta.title, allowed)
    expandTabKeys(item.pageName, allowed)
  })
  return tabs.filter(
    (tab) => allowed.has(tab.label) || allowed.has(tab.step)
  )
}

/** 是否具备某模块下页签权限（如 公共资料库 / 公共试卷库） */
export function hasTabPermission(pages, pageName, tabKey) {
  if (!tabKey) return false
  const node = findPage(pages, THEME_PAGE[pageName] || pageName)
  if (!node) return false
  const tabs = node.tabPages || []
  const split =
    node.meta &&
    (node.meta.tabSplit === true || node.meta.tabSplit === 'true')
  if (!tabs.length) {
    // 未拆页签：有父级页面即视为具备；已拆但未授权任何页签则不具备
    return !split
  }
  const allowed = new Set()
  tabs.forEach((item) => {
    const meta = item.meta || {}
    expandTabKeys(meta.tabKey, allowed)
    expandTabKeys(meta.title, allowed)
    expandTabKeys(item.pageName, allowed)
  })
  return allowed.has(tabKey)
}

/** 列表/选用范围：有公共库权限用 org（本机构+公开），否则仅 orgOwned */
export function publicPickScope(pages, pageName, tabKey, userName, superAdminName) {
  if (userName && superAdminName && userName === superAdminName) {
    return 'org'
  }
  return hasTabPermission(pages, pageName, tabKey) ? 'org' : 'orgOwned'
}

export function filterFieldMenus(menus, pages) {
  const node = findPage(pages, 'systemFields')
  if (!node || !node.meta) return menus
  const split = node.meta.tabSplit === true || node.meta.tabSplit === 'true'
  if (!split) return menus
  const allowed = new Set()
  ;(node.tabPages || []).forEach((item) => {
    const meta = item.meta || {}
    if (meta.tabKey) allowed.add(meta.tabKey)
    if (meta.title) allowed.add(meta.title)
  })
  return (menus || []).filter((item) => {
    return allowed.has(item.value) || allowed.has(item.label)
  })
}
